// ============================================================================
// LE SOCLE DES PANNEAUX SUPERPOSÉS — ce qu'une modale et un tiroir doivent au clavier.
//
// ⚠️ EXTRAIT DE `modal.tsx` (2026-08-18) quand le tiroir latéral en est devenu le second consommateur
// (règle de 2, §24.3). Recopier cette mécanique était le vrai danger : elle ne se voit pas à l'écran
// quand elle manque — un panneau sans piège à tabulation a l'air parfaitement normal, et c'est
// seulement au clavier qu'on remplit à l'aveugle le formulaire qui est DERRIÈRE lui.
//
// ⚠️ CE QU'ELLE TIENT, PARCE QUE CE DÉPÔT A DÉJÀ PAYÉ LE CONTRAIRE :
//   · le focus ENTRE à l'ouverture et REVIENT au déclencheur à la fermeture — jamais sur `<body>` ;
//   · il ne sort pas du panneau à la tabulation ;
//   · Échap ferme, et une saisie non enregistrée peut REFUSER la fermeture ;
//   · le fond ne défile plus tant que le panneau est ouvert.
// ============================================================================
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

/**
 * LE PLAN DU PANNEAU QUI M'HÉBERGE — `null` quand on est à même la page.
 *
 * ⚠️ IL EXISTE POUR UNE SEULE FAMILLE DE DÉFAUT : un élément qui sort en PORTAIL sur `document.body`
 * perd son contexte d'empilement. Il porte alors son plan absolu, et se retrouve DERRIÈRE le panneau
 * dans lequel il est pourtant né. Cas mesuré (2026-08-25) : l'infobulle du graphique de l'onglet
 * Stats — plan 680 par doctrine — rendue à l'intérieur de la fiche article, qui est une modale à 900.
 * Le code faisait exactement ce qu'il fallait, l'utilisateur ne voyait rien.
 *
 * ⚠️ CE N'EST PAS « REMONTER LES PLANS », et c'est ce qui compte. La doctrine de `Z_LAYERS` veut qu'une
 * bulle qui SUIT LE CURSEUR ne flotte jamais au-dessus d'un panneau qu'elle ne concerne pas — d'où son
 * plan bas. Se caler juste au-dessus de SON hôte respecte cette règle à la lettre : la bulle reste
 * couverte par tout ce qui s'ouvre PAR-DESSUS cet hôte. On corrige la référence, pas la hauteur.
 */
export const PlanDeSurcouche = createContext<number | null>(null);

/** Le plan à porter pour rester visible dans son hôte — `defaut` à même la page. */
export function usePlanAuDessusDeLHote(defaut: number): number {
  const hote = useContext(PlanDeSurcouche);
  return hote === null ? defaut : hote + 1;
}

/**
 * LA PILE DES SURCOUCHES OUVERTES — seule la DERNIÈRE ouverte répond à Échap.
 *
 * ⚠️ POURQUOI UNE PILE ET NON `stopPropagation`. Une modale et un panneau ouvert dans elle écoutent tous deux le
 *    `keydown` du DOCUMENT : sur une même cible, `stopPropagation` n'arrête pas les autres écouteurs, et c'est celui
 *    de la MODALE, posé le premier, qui passait le premier. Échap dans un sélecteur ouvert sur une fiche fermait la
 *    fiche avec, et la saisie avec elle. L'ordre d'ouverture est la seule vérité : la dernière couche ouverte est
 *    celle que l'utilisateur regarde.
 * ⚠️ ET UN ÉCHAP DÉJÀ TRAITÉ (`defaultPrevented`) N'EST PLUS À PERSONNE : une confirmation (`dialog.tsx`, en capture)
 *    ou un champ qui annule sa propre saisie l'ont consommé avant nous.
 */
const layers = new Map<symbol, number>();
let openedSoFar = 0;

/**
 * Inscrit la surcouche tant qu'elle est ouverte ; rend « suis-je la dernière ouverte ? ».
 * ⚠️ LE RANG SE PREND AU RENDU, PAS À L'EFFET : React exécute les effets de l'ENFANT avant ceux du parent, donc une
 *    modale montée dans le même rendu que le panneau qu'elle contient s'inscrivait APRÈS lui et passait pour la dernière
 *    ouverte. Le rendu, lui, va du parent à l'enfant — c'est l'ordre dans lequel les couches se posent à l'écran.
 */
export function useOverlayLayer(open: boolean): () => boolean {
  const id = useRef(Symbol('surcouche'));
  const rank = useRef(0);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) rank.current = ++openedSoFar;
  wasOpen.current = open;
  useEffect(() => {
    if (!open) return;
    const me = id.current;
    layers.set(me, rank.current);
    return () => { layers.delete(me); };
  }, [open]);
  return useCallback(() => {
    const mine = layers.get(id.current);
    if (mine === undefined) return false;
    for (const other of layers.values()) if (other > mine) return false;
    return true;
  }, []);
}

/** Cet Échap revient-il à cette surcouche ? — jamais traité, et elle est la dernière ouverte. */
export function escapeIsMine(e: { key: string; defaultPrevented: boolean }, isTop: () => boolean): boolean {
  return e.key === 'Escape' && !e.defaultPrevented && isTop();
}

/** Le sélecteur de ce qui peut prendre le focus — utilisé pour enfermer la tabulation dans le panneau. */
export const FOCUSABLES = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
/** Les champs de saisie d'un panneau — ce qui reçoit le focus d'un formulaire de création (`autoFocus: 'field'`). */
const FIELDS = 'input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled])';

/**
 * Ce que la touche doit provoquer dans un panneau ouvert.
 *
 * ⚠️ Extraite du gestionnaire pour être MESURABLE : ce paquet n'a pas d'environnement DOM, donc un
 * piège à tabulation écrit à l'intérieur d'un `addEventListener` n'est vérifié par rien — et c'est
 * exactement le genre de code qui casse sans bruit.
 * ⚠️ Le bouclage est la raison d'être de la fonction : sans lui, la tabulation SORT du panneau et
 * l'utilisateur remplit à l'aveugle le formulaire qui est derrière.
 */
export function toucheDansPanneau(
  e: { key: string; shiftKey: boolean },
  pos: { atFirst: boolean; atLast: boolean; count: number },
): 'close' | 'wrapToFirst' | 'wrapToLast' | null {
  if (e.key === 'Escape') return 'close';
  if (e.key !== 'Tab' || pos.count === 0) return null;
  if (e.shiftKey && pos.atFirst) return 'wrapToLast';
  if (!e.shiftKey && pos.atLast) return 'wrapToFirst';
  return null;
}

export interface OverlayPanelOptions {
  open: boolean;
  /** Fermeture demandée (Échap, voile, croix). ⚠️ L'appelant peut REFUSER — voir `blockClose`. */
  onClose: () => void;
  /**
   * OÙ LE FOCUS ENTRE à l'ouverture.
   * ⚠️ `panel` pour tout panneau de FORMULAIRE : saisir le premier contrôle pose le curseur dans un
   * champ que personne n'a demandé à modifier — on ouvre une fiche pour la LIRE. `first` reste le
   * défaut : sur une confirmation, saisir le bouton d'action est le bon geste. `field` pour un formulaire de
   * CRÉATION dont l'en-tête porte une croix : le premier CHAMP, jamais la croix — taper le nom est le geste attendu.
   */
  autoFocus?: 'first' | 'field' | 'panel';
  /**
   * Empêche la fermeture et DIT pourquoi.
   * ⚠️ Rendre `true` sans rien afficher ferait un panneau qui refuse de se fermer sans raison —
   * l'appelant doit poser sa propre confirmation avant de rendre `true`.
   */
  blockClose?: () => boolean;
}

/** Le comportement clavier et le verrou de défilement d'un panneau superposé, à brancher sur son `<div>`. */
export function useOverlayPanel({ open, onClose, autoFocus = 'first', blockClose }: OverlayPanelOptions) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const renderAt = useRef<HTMLElement | null>(null);
  const isTop = useOverlayLayer(open);

  const close = useCallback(() => {
    if (blockClose?.()) return;
    onClose();
  }, [blockClose, onClose]);

  // ⚠️ LE DÉCLENCHEUR EST RETENU À L'OUVERTURE : c'est à lui que le focus revient. Sans ça, fermer un
  // panneau laisse le focus sur `<body>` et le clavier repart du haut de la page.
  useEffect(() => {
    if (!open) return;
    renderAt.current = document.activeElement as HTMLElement | null;
    const target = autoFocus === 'panel'
      ? panelRef.current
      : (panelRef.current?.querySelector<HTMLElement>(autoFocus === 'field' ? FIELDS : FOCUSABLES) ?? panelRef.current);
    target?.focus({ preventScroll: true });
    return () => renderAt.current?.focus({ preventScroll: true });
  }, [open, autoFocus]);

  // ⚠️ LE FOND NE DÉFILE PLUS : sans ça, la molette fait glisser la page DERRIÈRE le panneau, et on
  // revient sur un écran qui n'est plus là où on l'avait laissé.
  useEffect(() => {
    if (!open) return;
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = avant; };
  }, [open]);

  // Échap ferme ; la tabulation reste ENFERMÉE dans le panneau.
  useEffect(() => {
    if (!open) return;
    const surTouche = (e: KeyboardEvent) => {
      // ⚠️ UNE COUCHE OUVERTE PAR-DESSUS (un sélecteur, une confirmation) a la main : Échap et la tabulation sont à elle.
      if (!panelRef.current || !isTop()) return;
      if (e.key === 'Escape' && !escapeIsMine(e, isTop)) return;
      const targets = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLES));
      const premier = targets[0];
      const dernier = targets[targets.length - 1];
      const what = toucheDansPanneau(e, {
        atFirst: document.activeElement === premier,
        atLast: document.activeElement === dernier,
        count: targets.length,
      });
      // ⚠️ L'ÉCHAP EST MARQUÉ TRAITÉ : une couche au-dessous, ou un écouteur posé plus tard, ne fermera pas en plus.
      if (what === 'close') { e.preventDefault(); e.stopPropagation(); close(); return; }
      if (what === 'wrapToLast') { e.preventDefault(); dernier?.focus(); }
      else if (what === 'wrapToFirst') { e.preventDefault(); premier?.focus(); }
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [open, close, isTop]);

  return { panelRef, close };
}
